import { Color, Spacing } from '@persona-id/design-tokens';
import DatabaseIcon from '@persona-id/design-tokens/icons/DatabaseIcon';
import { DateTime } from 'luxon';
import styled from 'styled-components';

import ContextMenu from 'dashboard/components/ContextMenu';
import HoverCopyableField from 'dashboard/components/HoverCopyableField';
import { MonoSpan } from 'dashboard/components/Typography';
import TableLayout, {
  TableActionTD,
  TableLinkTR,
  TableNullState,
  TableTD,
  TableTH,
} from 'dashboard/layouts/TableLayout';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { InsightQuery } from 'dashboard/models/insight-queries';
import Spacer from 'design-system/components/Spacer';
import { DATE_AND_TIME_FORMAT, TIMEZONE_NAME_ABBREVIATED } from 'lib/constants/luxonFormats';

const InsightQueriesTableContainer = styled('div')`
  margin-bottom: ${Spacing[2]};
`;

interface InsightQueriesTableProps {
  insightQueries: InsightQuery[];
  sortParams: SortParams;
  canSort: boolean;
  loading: boolean;
  handleSortUpdate: (sortParams: SortParams) => void;
  onArchiveClick: (id: string) => void;
}

const InsightQueriesTable = ({
  insightQueries = [],
  sortParams,
  canSort,
  handleSortUpdate,
  onArchiveClick,
}: InsightQueriesTableProps) => {
  const handleColumnHeaderClick = (columnName: string) => {
    if (!canSort) {
      return;
    }

    handleSortUpdate({
      sort: columnName,
      shouldSortDescending:
        columnName === sortParams.sort ? !sortParams.shouldSortDescending : true,
    });
  };

  const insightQueriesRows = insightQueries.map((query: InsightQuery, key: number) => (
    <InsightQueryTableRow key={key} insightQuery={query} onArchiveClick={onArchiveClick} />
  ));

  return (
    <InsightQueriesTableContainer>
      <TableLayout
        isLoading={false}
        isEmpty={insightQueries.length === 0}
        showHeader={true}
        nullState={
          <TableNullState>
            <DatabaseIcon size={64} color={Color.BorderIconSecondary} />
            <Spacer height={3} />
            <div>
              View previously saved queries. Launch the editor and begin querying, save it, then
              find it here.
            </div>
            <Spacer height={5} />
            <a href='' target='_blank' rel='noreferrer'>
              Learn more about insight queries
            </a>
          </TableNullState>
        }
        headerTR={
          <>
            <TableTH
              onClick={() => handleColumnHeaderClick('name')}
              canSort={canSort}
              isSorted={sortParams.sort === 'name'}
              isDescending={sortParams.shouldSortDescending}
              id='insight-query-table__name-header'
            >
              Name
            </TableTH>
            <TableTH>Insight Query ID</TableTH>
            <TableTH>Status</TableTH>
            <TableTH
              onClick={() => handleColumnHeaderClick('created_at')}
              canSort={canSort}
              isSorted={sortParams.sort === 'created_at'}
              isDescending={sortParams.shouldSortDescending}
              id='insight-query-table__created-at-header'
            >
              {`Created at (${DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)})`}
            </TableTH>
            <TableTH
              onClick={() => handleColumnHeaderClick('updated_at')}
              canSort={canSort}
              isSorted={sortParams.sort === 'updated_at'}
              isDescending={sortParams.shouldSortDescending}
              id='insight-query-table__updated-header'
            >
              {`Updated at (${DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)})`}
            </TableTH>
            {/* TODO(mateo): add creator column */}
            <TableTH isSticky>{/* Actions column */}</TableTH>
          </>
        }
      >
        {insightQueriesRows}
      </TableLayout>
    </InsightQueriesTableContainer>
  );
};

const InsightQueryTableRow = ({
  insightQuery,
  onArchiveClick,
}: {
  insightQuery: InsightQuery;
  onArchiveClick: (id: string) => void;
}) => {
  const {
    status,
    name,
    'created-at': createdAt,
    'updated-at': updatedAt,
  } = insightQuery.attributes;

  return (
    <TableLinkTR
      to={`/dashboard/insight-query-tool/${insightQuery.id}`}
      data-test={`inquiry-table-row__${insightQuery.id}`}
      id='insigth-query-table__row'
    >
      <TableTD>{name}</TableTD>
      <TableTD>
        <HoverCopyableField copyValue={insightQuery.id} confirmationText='Copied Insight Query ID'>
          <MonoSpan>{insightQuery.id}</MonoSpan>
        </HoverCopyableField>
      </TableTD>
      <TableTD>{status}</TableTD>
      <TableTD>{DateTime.fromISO(createdAt).toFormat(DATE_AND_TIME_FORMAT)}</TableTD>
      <TableTD>{DateTime.fromISO(updatedAt).toFormat(DATE_AND_TIME_FORMAT)}</TableTD>
      <TableActionTD>
        <ContextMenu
          items={[
            {
              id: 'archive',
              onClick: () => onArchiveClick(insightQuery.id),
              variant: 'basic' as const,
              label: 'Archive',
            },
          ]}
          renderItem={(item) => (
            <ContextMenu.Item
              key={item.id}
              id={`insight-query-table-row__${insightQuery.id}--action-menu-${item.id}`}
              onClick={(e) => {
                // Needed to avoid triggering row click below
                e.stopPropagation();
                e.preventDefault();
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                item.onClick();
              }}
              variant={item.variant}
            >
              {item.label}
            </ContextMenu.Item>
          )}
        />
      </TableActionTD>
    </TableLinkTR>
  );
};

export default InsightQueriesTable;
